/** @jsx tsx */
import { tsx } from "@dojo/framework/core/vdom";
import { Dimensions } from "@dojo/framework/core/meta/Dimensions";
import { WidgetBase } from "@dojo/framework/core/WidgetBase";
import { cloneDeep, defaultsDeep } from "lodash";
import * as Constants from "../../Constants";
import { degToRad, generateUUID, radToDeg, roundNumber } from "../../store/reducers/util";
import { ConnectionShapeContainer } from "../containers/ShapeContainer";
import { CanvasMode } from "../interfaces/model/enumerations/CanvasMode";
import { FillTypes, GeometryTypes, IPageObjectTypes } from "../interfaces/model/enumerations/index";
import { PathOperationTypes } from "../interfaces/model/enumerations/PathOperationTypes";
import { ICustomGeometry } from "../interfaces/model/geometry/CustomGeometry";
import { IPresetGeometry } from "../interfaces/model/geometry/PresetGeometry";
import { IDrawLineTo, IMovePathTo } from "../interfaces/model/geometry/ShapePath";
import { IShapePositionCoordinate } from "../interfaces/model/geometry/ShapePositionCoordinate";
import { IDrawShapeProperties } from "../interfaces/model/index";
import { IConnectionShape } from "../interfaces/model/PageObjects/ConnectionShape";
import { cancelCurrentOperation, isSnapToGridDisabled, maintainAspectRatio } from "../keyUtil";
import { IDragResults, IPosition } from "../meta/Drag/CsiDragController";
import { CsiDrag } from "../meta/Drag/index";
import { getSnapToGridValue, getZoomValue } from "../selectors/stateAwareConstants";
import PageHelper from "../../store/PageHelper";
import { adjustPositionForRoot, getDistance, getRadians } from "../util";
import { ICreateShapeRenderProps } from "./CanvasCreateShape";

/**
 * Canvas Draw page for connection shape.
 * Provides base component for drawing shapes on canvas. Here we use optimistic rendering,
 * a shape is rendered before adding to the state. This way user can see shape before it get's added,
 * as well it will get added in single operation.
 *
 * During drawing of shape full canvas is used as dimensions of shape (spPr.ext).
 * Once drawing is complete and before adding shape to page, we update it with actual offsets.
 * Offsets are calculated based on Min X & Y points.
 */
export class CreateConnectionShape extends WidgetBase<ICreateShapeRenderProps> {
	/**
     * {IShape} Shape which is being drawn.
     */
	private shape: IConnectionShape | undefined;

	/**
     * Flag to indicate if dragging is possible or not
     */
	private _dragging = false;

	/**
     * Start positon of drawing.
     */
	private _drawStartPosition: IPosition | undefined = undefined;

	/**
     * Virtual DOM key for drawing board.
     */
	private key = "drawingBoard";

	/**
     * Holds event handlers registered for this widget. When widget is detached, these handlers will be removed.
     */
	private _boundHandlers: any[] = [];

	constructor() {
		super();
		this._boundHandlers = [];

		// Add event listeners for create connection shape
		[
			{ event: "pointerup", func: this._onPointerUp.bind(this) },
		].forEach((object) => {
			document.addEventListener(object.event, object.func);
			this._boundHandlers.push(object);
		});
	}

	/**
     * Pointer down event for shape
     * @param event {PointerEvent}
     */
	protected _onPointerDown(event: PointerEvent) {
		this._dragging = true;

		// consider client offset for draw page.
		const dimensions = this.meta(Dimensions)
			.get(this.key);

		const boundingBox = this.properties.api.canvasContainerEl;
		const boundingBoxBound = boundingBox.getBoundingClientRect();

		this._drawStartPosition = {
			x: getZoomValue(event.clientX - boundingBoxBound.left),
			y: getZoomValue(event.clientY - boundingBoxBound.top),
		};

		if (!isSnapToGridDisabled(event)) {
			this._drawStartPosition = {
				x: getSnapToGridValue(this._drawStartPosition.x),
				y: getSnapToGridValue(this._drawStartPosition.y),
			};
		}

		// if undefined it is custom shape a curve will be drawn.
		const shapeGeometry: IPresetGeometry | ICustomGeometry = {
			pathLst: [
				{
					operations: [
						{
							pt: { x: this._drawStartPosition.x, y: this._drawStartPosition.y },
							type: PathOperationTypes.MOVE_TO,
						},
						{
							pt: { x: this._drawStartPosition.x, y: this._drawStartPosition.y },
							type: PathOperationTypes.LINE_TO,
						},
					],
				},
			],
			type: GeometryTypes.CUSTOM,
		};

		// Create basic structure for the shape.
		this.shape = {
			allowsArrows: true,
			noChangeAspect: false,
			shapeId: generateUUID(),
			spPr: {
				fill: {
					color: "none",
					type: FillTypes.SOLID_FILL,
				},
				geometry: shapeGeometry,
				xfrm: {
					ext: { cx: dimensions.size.width, cy: dimensions.size.height },
					flipH: false,
					flipV: false,
					off: {
						x: 0,
						y: 0,
					},
					rot: 0,
				},
			},
			type: IPageObjectTypes.CONNECTION_SHAPE,
		};

		// Let's apply default shape properties for the shape.
		const defaults = Constants.DEFAULT_SHAPE_PROPERTIES;
		const userDefaults = this.properties.selection.defaultShapeProperties;
		this.shape = defaultsDeep(this.shape, userDefaults, defaults);
	}

	/**
     * Clean up on detach
     */
	protected onDetach(): void {
		this._boundHandlers.forEach((object) => document.removeEventListener(object.event, object.func));
		this.meta(CsiDrag)
			.remove(this.key);
	}

	/**
     * Pointer up event for shape
     * @param event {PointerEvent}
     */
	protected _onPointerUp() {
		if (this._dragging) {
			const shapeDrawTools = this.properties.selection.shapeDrawTools as IDrawShapeProperties;
			const { api } = this.properties;

			// Reset canvas mode to select and add shape to the page.
			api.canvasApi.setCanvasMode(CanvasMode.SELECT);

			this._completeShape();

			// Add shape to actual pageObjects if it has valid height and width
			if (this.shape && (this.shape.spPr.xfrm.ext.cx || this.shape.spPr.xfrm.ext.cy)) {
				api.pageApi.addPageObject(this.shape);
			}

			// If editOnComplete is true, then on drag complete shape should be in edit mode
			if (shapeDrawTools && shapeDrawTools.editOnComplete) {
				this.properties.api.canvasApi.editPageObject();
			}
			// Reset shape draw tools on shape draw complete
			api.selectionApi.setShapeDrawTool();
			this.shape = undefined;
			this._dragging = false;
		}
	}

	/**
     * Complete shape by calculating offsets and apply offsets to geometry.
     */
	protected _completeShape() {
		const { shape } = this;
		if (shape) {
			// const dimensions = this.meta(Dimensions).get("path_tempShape");
			const shapeGeometry: ICustomGeometry = cloneDeep(shape.spPr.geometry);
			const pathLst = shapeGeometry.pathLst;
			const pathOperationLst = pathLst && pathLst[0] && pathLst[0].operations;
			const pt1 = pathOperationLst[0] as IDrawLineTo;
			const pt2 = pathOperationLst[1] as IDrawLineTo;
			const xfrm = shape.spPr.xfrm;

			const {
				ext,
				off,
			} = xfrm;

			const { pt } = pt1;
			const ptt2 = pt2.pt;
			off.x = Math.min(pt.x as number, ptt2.x as number);
			off.y = Math.min(pt.y as number, ptt2.y as number);
			xfrm.ext.cx = roundNumber(Math.abs((pt.x as number - (ptt2.x as number))));
			xfrm.ext.cy = roundNumber(Math.abs((pt.y as number - (ptt2.y as number))));

			pt.x = pt.x === off.x ? 0 : ext.cx;
			pt.y = pt.y === off.y ? 0 : ext.cy;
			ptt2.x = ptt2.x === off.x ? 0 : ext.cx;
			ptt2.y = ptt2.y === off.y ? 0 : ext.cy;

			adjustPositionForRoot(this.properties.api.canvasContainerEl, off);

			shapeGeometry.pathLst = pathLst;
			this.shape.spPr.geometry = shapeGeometry;
		}
	}

	/**
     * Pointer drag event for shape
     * @param drag {DragResults}
     */
	protected _onPointerDrag(drag: IDragResults) {
		const isEscapeKeyPress = cancelCurrentOperation(drag.keyData);
		if (isEscapeKeyPress) {
			this._dragging = false;
			this.shape = undefined;
			return;
		}
		if (drag.outOfEditor && !drag.isDragging) {
			// while drawing shape if pointer goes out of editor and stops dragging
			// then shape should be drawn within the editor's boundary. Hence calling
			// pointer up explicitly to complete shape rendering.
			this._onPointerUp();
			return;
		}
		if (!this._dragging || !drag.delta.x || !drag.delta.y) {
			return;
		}
		this.shape = cloneDeep(this.shape);
		if (this.shape) {
			if (drag.current && this._drawStartPosition) {
				// let's set drag.current as 2nd point in geometry.
				const shapeGeometry: ICustomGeometry = this.shape.spPr.geometry;
				const pathLst = shapeGeometry.pathLst;
				const pathOperationLst = pathLst && pathLst[0] && pathLst[0].operations;

				const isSnapToGridEnabled = !isSnapToGridDisabled(drag.keyData);
				const pt: IShapePositionCoordinate = {
					x: (isSnapToGridEnabled ? getSnapToGridValue(drag.current.x) : drag.current.x),
					y: (isSnapToGridEnabled ? getSnapToGridValue(drag.current.y) : drag.current.y),
				};

				const isMaintainAspectRatio = maintainAspectRatio(drag.keyData);

				if (isMaintainAspectRatio) {
					const moveTo = pathOperationLst[0] as IMovePathTo;
					const pt1 = moveTo.pt as IPosition;
					const pt2 = pt as IPosition;

					let angleRad = getRadians(pt1 as IPosition, pt2 as IPosition);
					const angleDeg = radToDeg(angleRad);

					// Check if angle is 45 and snap to nearest 0,45,90,135,180,225,270,315 degree angle.
					if (angleDeg % 45 !== 0) {
						const alpha = Math.round(angleDeg / 45);

						angleRad = degToRad(alpha * 45);
						const distance = getDistance(pt1, pt2);
						pt.x = pt1.x + Math.cos(angleRad) * distance;
						pt.y = pt1.y + Math.sin(angleRad) * distance;
					}
				}
				const lineTo = pathOperationLst[1] as IDrawLineTo;
				lineTo.pt = pt;
			}
		}
	}

	/**
	 * Override WidgetBase#render to produce a virtual DOM tree.
	 * @returns {HNode} Each time render() executes, it should build the entire virtual DOM tree.
	 */
	protected render() {
		const dragResult = this.meta(CsiDrag)
			.get(this.key, IPageObjectTypes.CONNECTION_SHAPE);
		this._onPointerDrag(dragResult);

		if (this.properties.canvasEditor.mode === CanvasMode.DRAW) {
			return (
				<div
					classes="c8_canvas_modeDraw"
					key={this.key}
					onpointerdown={this._onPointerDown}
					styles={{ zIndex: `${Constants.MAX_Z_INDEX}` }}
				>
					{this.shape && (
						<ConnectionShapeContainer
							key={Constants.TEMP_SHAPE}
							pageObject={this.shape}
							partOfGroup={false}
						/>
					)}
				</div>
			);
		}
		return null;
	}
}

export default CreateConnectionShape;
